import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import DynamicFormRenderer from "../components/forms/DynamicFormRenderer";
import { useValuations } from "../hooks/useApi";
import {
  trackFormSubmission,
  trackItemCreated,
  ITEM_TYPES,
} from "../utils/analytics";

const NewValuation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { createItem } = useValuations();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  // Template mapping from URL parameter to actual template IDs
  const templateMapping = {
    basic: "valuation-basic-v1",
    "step-one": "valuation_step_one_v1",
  };

  // Get template from URL parameter, default to basic
  const templateParam = searchParams.get("template") || "basic";
  const templateId = templateMapping[templateParam] || templateMapping.basic;

  // Get template display name for the header
  const templateNames = {
    basic: "Basic Property Valuation",
    "step-one": "Step One Property Valuation Report v1",
  };
  const templateDisplayName =
    templateNames[templateParam] || templateNames.basic;

  const handleSubmit = async (data) => {
    try {
      setIsSubmitting(true);

      // Track form submission attempt
      trackFormSubmission(ITEM_TYPES.VALUATION, data);

      const result = await createItem(data);

      // Track successful item creation
      trackItemCreated(ITEM_TYPES.VALUATION, data);

      navigate("/valuations", {
        state: { message: "Valuation created successfully!" },
      });
    } catch (error) {
      console.error("Error creating valuation:", error);
      throw error; // Let the form handle the error display
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/valuations");
  };

  const handleFormDataChange = (newData) => {
    setFormData(newData);
  };

  const handleValidationChange = (errors) => {
    setValidationErrors(errors);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Back to Valuations
          </button>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">
          Create New Valuation
        </h1>
        <p className="text-gray-600 mt-1">
          {templateDisplayName} - Fill in the details below to create a new
          valuation
        </p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <DynamicFormRenderer
          templateId={templateId}
          fieldsId="valuation-fields"
          data={formData}
          onDataChange={handleFormDataChange}
          onSave={handleSubmit}
          onValidationChange={handleValidationChange}
          autoSave={false}
          showProgress={true}
          className="max-w-6xl"
        />

        {/* Form Actions */}
        <div className="flex justify-between items-center pt-6 border-t border-gray-200 mt-6">
          <div className="text-sm text-gray-600">
            {Object.keys(validationErrors).length > 0 && (
              <span className="text-red-600">
                Please fix {Object.keys(validationErrors).length} validation
                error(s) before saving
              </span>
            )}
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => handleSubmit(formData)}
              disabled={
                Object.keys(validationErrors).length > 0 || isSubmitting
              }
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Creating..." : "Create Valuation"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewValuation;
